/**
 * AuthErrorMessage Component
 * Displays user-friendly error messages for auth failures
 */

interface AuthErrorMessageProps {
    errorCode?: string;
    message: string;
    onRetry?: () => void;
    showSupport?: boolean;
}

const ERROR_MESSAGES: Record<string, { title: string; action: string }> = {
    INVITE_EXPIRED: {
        title: 'Invitation Expired',
        action: 'Contact Support',
    },
    INVITE_003: {
        title: 'Invitation Limit Reached',
        action: 'Contact Support',
    },
    INVITE_001: {
        title: 'Invalid Invitation',
        action: 'Contact Support',
    },
    INVITE_002: {
        title: 'Invitation Expired',
        action: 'Contact Support',
    },
    DOMAIN_001: {
        title: 'Domain Mismatch',
        action: 'Contact Support',
    },
    AUTH_FAILED: {
        title: 'Authentication Failed',
        action: 'Try Again',
    },
    AUTH_002: {
        title: 'Access Denied',
        action: 'Contact Support',
    },
    AUTH_003: {
        title: 'Access Revoked',
        action: 'Contact Support',
    },
    NETWORK_ERROR: {
        title: 'Connection Error',
        action: 'Retry',
    },
};

export default function AuthErrorMessage({
    errorCode,
    message,
    onRetry,
    showSupport = true,
}: AuthErrorMessageProps) {
    const errorInfo = errorCode ? ERROR_MESSAGES[errorCode] : null;
    const title = errorInfo?.title || 'Error';
    const actionLabel = errorInfo?.action || 'OK';

    return (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex items-start">
                <div className="flex-shrink-0">
                    <svg
                        className="h-5 w-5 text-red-400"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                    >
                        <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                            clipRule="evenodd"
                        />
                    </svg>
                </div>
                <div className="ml-3 flex-1">
                    <h3 className="text-sm font-medium text-red-800">{title}</h3>
                    <p className="mt-2 text-sm text-red-700">{message}</p>
                    <div className="mt-4">
                        {onRetry && actionLabel === 'Try Again' && (
                            <button
                                onClick={onRetry}
                                className="bg-red-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-700 transition-colors"
                            >
                                {actionLabel}
                            </button>
                        )}
                        {showSupport && actionLabel === 'Contact Support' && (
                            <a
                                href="mailto:support@intranethealthcheck.com"
                                className="inline-block bg-red-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-700 transition-colors"
                            >
                                {actionLabel}
                            </a>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
